import {
  Button,
  Checkbox,
  cn,
  FormField,
  FormFieldHint,
  FormFieldLabel,
  Progress,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { ArchiveIcon, PlusIcon } from './icons';

export interface ProgramBenefitsPageProps {
  benefitOptions: readonly string[];
  selectedBenefits: string[];
  onToggleBenefit: (benefit: string) => void;
  onAddBenefit: () => void;

  additionalPaymentOptions: readonly string[];
  selectedAdditionalPayments: string[];
  onToggleAdditionalPayment: (payment: string) => void;
  onAddAdditionalPayment: () => void;

  progress: number;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function ProgramBenefitsPage({
  benefitOptions,
  selectedBenefits,
  onToggleBenefit,
  onAddBenefit,
  additionalPaymentOptions,
  selectedAdditionalPayments,
  onToggleAdditionalPayment,
  onAddAdditionalPayment,
  progress,
  onBack,
  onSubmit,
  isSubmitting,
}: ProgramBenefitsPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                <ArchiveIcon className="size-4" />
              </div>
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                Criação de programa
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Benefícios
            </p>
          </div>

          <Progress value={progress} className="h-2 bg-secondary/40" />

          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <FormField>
              <FormFieldLabel>Benefícios</FormFieldLabel>
              <CheckboxCardGroup
                options={benefitOptions}
                selected={selectedBenefits}
                onToggle={onToggleBenefit}
              />
              {/* EU-709 RN-06 — a etapa pode ficar inteiramente vazia e o
                  `Avançar` segue ativo; a marca é o que o Figma pede para
                  deixar isso explícito ao consultor. */}
              <FormFieldHint>Esse campo é opcional</FormFieldHint>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={onAddBenefit}
                className="self-start"
              >
                <PlusIcon />
                Adicionar novo
              </Button>
            </FormField>

            <FormField>
              <FormFieldLabel>Pagamento adicionais</FormFieldLabel>
              <CheckboxCardGroup
                options={additionalPaymentOptions}
                selected={selectedAdditionalPayments}
                onToggle={onToggleAdditionalPayment}
              />
              <FormFieldHint>Esse campo é opcional</FormFieldHint>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={onAddAdditionalPayment}
                className="self-start"
              >
                <PlusIcon />
                Adicionar novo
              </Button>
            </FormField>

            <div className="flex flex-row items-center justify-between pt-3">
              <Button type="button" variant="outline" size="lg" onClick={onBack}>
                Voltar
              </Button>
              <Button type="submit" size="lg" loading={isSubmitting}>
                Avançar
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

interface CheckboxCardGroupProps {
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}

function CheckboxCardGroup({ options, selected, onToggle }: CheckboxCardGroupProps) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option);
        return (
          <CheckboxCardPill
            key={option}
            label={option}
            checked={isChecked}
            onCheckedChange={() => onToggle(option)}
          />
        );
      })}
    </div>
  );
}

interface CheckboxCardPillProps {
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
}

function CheckboxCardPill({ label, checked, onCheckedChange }: CheckboxCardPillProps) {
  return (
    <label
      className={cn(
        'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-foreground hover:border-primary'
      )}
    >
      <Checkbox checked={checked} onCheckedChange={onCheckedChange} className="size-4" />
      <span>{label}</span>
    </label>
  );
}
